/**
 * Delimiter scanning for text that mixes prose with LaTeX.
 *
 * Lives here (rather than in shared-ui) so the renderer and the backend
 * `InvalidLatex` diagnosis agree on what counts as math — they used to disagree,
 * and content that rendered wrong was invisible to the check.
 *
 * ── Why this isn't just indexOf('$') ──────────────────────────────────────
 * Question text is full of currency: "Jonah has $750 … He now has $437.50."
 * A naive left-to-right pairing treats the currency dollars as math delimiters
 * and swallows the prose between them, so `$\frac{1}{4}$` ends up *outside*
 * math mode and renders as literal backslash-frac. The rules below let a
 * currency `$` fall through as a literal without an author having to escape it:
 *
 *   R1  A `$` preceded by an odd run of backslashes is literal (`\$`), never a
 *       delimiter. Text segments render it as `$`; math segments keep the `\$`
 *       because that is what KaTeX wants.
 *   R2  Only the *immediately next* unescaped `$` can close an inline `$`.
 *       A math body therefore never contains a bare `$`, which is what stops
 *       long-range pairing across a sentence.
 *   R3  A body containing no LaTeX signal (`\ ^ _ { }`) is rejected when it is
 *       whitespace-padded. Bodies *with* a signal are exempt, so deliberately
 *       padded math (`$ \frac{1}{2} $`) still renders while an accidental
 *       `$750. He spends $` pairing does not.
 *   R4  A signal-free inline body may not span a newline.
 *   R5  A `$` that fails the above is emitted as one literal character and
 *       scanning resumes immediately after it. (The previous implementation
 *       dumped the entire remainder of the string as plain text, so a single
 *       stray `$` disabled every formula downstream of it.)
 *
 * `$$…$$`, `\[…\]` and `\(…\)` are unambiguous and are matched as-is.
 */

export type LatexSegment =
    | { type: 'text'; value: string }
    | { type: 'inline'; value: string }
    | { type: 'display'; value: string };

/** A segment plus its half-open `[start, end)` offsets in the source string. */
export type LatexSpan = LatexSegment & { start: number; end: number };

/** Characters that only appear in LaTeX, never in a currency amount or prose. */
const MATH_SIGNAL = /[\\^_{}]/;

/** True when the `$` at `idx` is escaped — preceded by an odd run of `\`. */
export function isEscapedDollar(text: string, idx: number): boolean {
    let backslashes = 0;
    for (let i = idx - 1; i >= 0 && text[i] === '\\'; i--) backslashes++;
    return backslashes % 2 === 1;
}

/** Index of the next occurrence of `token` at/after `from` that isn't escaped. */
function indexOfUnescaped(text: string, token: string, from: number): number {
    for (let i = text.indexOf(token, from); i !== -1; i = text.indexOf(token, i + 1)) {
        if (!isEscapedDollar(text, i)) return i;
    }
    return -1;
}

/**
 * Would `$body$` have been written as math on purpose? Applies R3/R4 — the test
 * that separates real inline math from two currency symbols that happen to sit
 * on either side of some prose.
 */
export function isPlausibleInlineMath(body: string): boolean {
    if (!body) return false;
    if (MATH_SIGNAL.test(body)) return true; // real LaTeX — padding is fine
    if (body !== body.trim()) return false; // R3
    if (body.includes('\n')) return false; // R4
    return true;
}

/** In plain-text runs, `\$` is just a dollar sign. */
function unescapeDollars(text: string): string {
    return text.replace(/\\\$/g, '$');
}

/**
 * Split `text` into text/inline/display spans carrying source offsets. The
 * spans tile the input contiguously, which is what lets `safeTruncate` cut on
 * the same boundaries the renderer will use.
 */
export function scanLatex(text: string): LatexSpan[] {
    if (!text) return [];
    const spans: LatexSpan[] = [];
    let textStart = 0;
    let i = 0;

    const flushText = (end: number) => {
        if (end > textStart) {
            spans.push({
                type: 'text',
                value: unescapeDollars(text.substring(textStart, end)),
                start: textStart,
                end,
            });
        }
    };

    const pushMath = (type: 'inline' | 'display', bodyStart: number, bodyEnd: number, closeLen: number) => {
        flushText(i);
        spans.push({ type, value: text.substring(bodyStart, bodyEnd), start: i, end: bodyEnd + closeLen });
        i = bodyEnd + closeLen;
        textStart = i;
    };

    while (i < text.length) {
        const ch = text[i];

        // \[ … \]  (display)  and  \( … \)  (inline)
        if (ch === '\\' && (text[i + 1] === '[' || text[i + 1] === '(')) {
            const isDisplay = text[i + 1] === '[';
            const close = isDisplay ? '\\]' : '\\)';
            const end = text.indexOf(close, i + 2);
            if (end !== -1) {
                pushMath(isDisplay ? 'display' : 'inline', i + 2, end, close.length);
            } else {
                i += 2; // unclosed → literal, keep scanning (R5)
            }
            continue;
        }

        if (ch === '$' && !isEscapedDollar(text, i)) {
            // `$$` wins over `$` at the same offset.
            if (text[i + 1] === '$') {
                const end = indexOfUnescaped(text, '$$', i + 2);
                if (end !== -1) {
                    pushMath('display', i + 2, end, 2);
                } else {
                    i += 2;
                }
                continue;
            }

            const end = indexOfUnescaped(text, '$', i + 1); // R2
            if (end !== -1 && isPlausibleInlineMath(text.substring(i + 1, end))) {
                pushMath('inline', i + 1, end, 1);
            } else {
                i += 1; // R5
            }
            continue;
        }

        i++;
    }

    flushText(text.length);
    return spans;
}

/** Segments without offsets — what a renderer needs. */
export function parseLatexSegments(text: string): LatexSegment[] {
    return scanLatex(text).map(({ type, value }) => ({ type, value }) as LatexSegment);
}

/**
 * The LaTeX bodies in `text`, for validation. Only real math is returned, so a
 * sentence full of currency no longer looks like a pile of broken formulas.
 */
export function extractLatexBodies(text: string): string[] {
    return scanLatex(text)
        .filter(s => s.type !== 'text' && s.value.trim())
        .map(s => s.value);
}

/**
 * Truncate to roughly `maxLen` characters without cutting a formula in half.
 * A math span that straddles the limit is kept whole when it ends within 30
 * characters of it, and otherwise dropped entirely.
 */
export function safeTruncateLatex(text: string, maxLen: number): string {
    if (!text || text.length <= maxLen) return text;

    let cut = 0;
    for (const span of scanLatex(text)) {
        if (span.end <= maxLen) {
            cut = span.end;
            continue;
        }
        // This span straddles the limit.
        if (span.type === 'text') {
            cut = maxLen; // safe to cut mid-prose
        } else {
            cut = span.end <= maxLen + 30 ? span.end : span.start;
        }
        break;
    }

    return text.substring(0, cut).trimEnd() + '...';
}
